import { useState } from "react";
import { toast } from "sonner";
import { MapPin, CheckCircle2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { GeoMiniMapa } from "@/components/common/GeoMiniMapa";
import { UbicacionGuardada } from "@/components/common/UbicacionGuardada";

// Control OPCIONAL de UN SOLO PUNTO (mismo patrón que geo_visita): getCurrentPosition una vez,
// sin watchPosition / intervalos / background. NO persiste: reporta el punto al padre vía onCapture.
// Si `saved` viene con sello, muestra el registro ya guardado (lat/lng + sello + mini-mapa OSM).
export function GeoPuntoOpcional({
  label = "Marcar ubicación (opcional)",
  microtext,
  value,
  saved,
  onCapture,
  onClear,
  testidPrefix = "geo-punto",
}) {
  const [activar, setActivar] = useState(false);
  const [busy, setBusy] = useState(false);

  const capturar = () => {
    if (!("geolocation" in navigator)) { toast.error("Tu dispositivo no permite geolocalización."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onCapture?.({ lat: pos.coords.latitude, lng: pos.coords.longitude, exactitud: pos.coords.accuracy });
        setBusy(false);
        toast.success("Ubicación capturada.");
      },
      () => { toast.error("No se pudo obtener tu ubicación. Activa el permiso de GPS."); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // 1) Registro YA GUARDADO (con sello) — incluye botón "Abrir en mapas"
  if (saved && saved.lat != null) {
    return <UbicacionGuardada geo={saved} label="Ubicación confirmada" testid={`${testidPrefix}-guardado`} />;
  }

  // 2) Punto recién capturado (aún no persistido por el padre)
  if (value && value.lat != null) {
    return (
      <div data-testid={`${testidPrefix}-capturado`} className="rounded-md border border-teal-200 bg-teal-50 p-3 text-xs text-teal-800">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4" /> Ubicación lista para guardar</span>
          <Button data-testid={`${testidPrefix}-quitar`} type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2 text-slate-500"
            onClick={() => { setActivar(false); onClear?.(); }}>
            <X className="h-3.5 w-3.5" /> Quitar
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-teal-700">📍 {Number(value.lat).toFixed(5)}, {Number(value.lng).toFixed(5)}{value.exactitud != null ? ` · ±${Math.round(value.exactitud)} m` : ""}</p>
        <div className="mt-2"><GeoMiniMapa lat={Number(value.lat)} lng={Number(value.lng)} testid={`${testidPrefix}-mapa`} /></div>
      </div>
    );
  }

  // 3) Control inicial (opcional)
  return (
    <div data-testid={`${testidPrefix}-control`} className="rounded-md border border-slate-200 p-3">
      <label className="flex items-start gap-2 text-xs font-medium text-slate-700">
        <Checkbox data-testid={`${testidPrefix}-toggle`} checked={activar} onCheckedChange={setActivar} />
        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" /> {label}</span>
      </label>
      {activar && (
        <div className="mt-2 space-y-2">
          {microtext && <p className="rounded bg-slate-50 p-2 text-[11px] text-slate-600">{microtext}</p>}
          <Button data-testid={`${testidPrefix}-obtener`} type="button" size="sm" onClick={capturar} disabled={busy} className="gap-1.5 bg-primary hover:bg-sidebar-hover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Obtener ubicación
          </Button>
        </div>
      )}
    </div>
  );
}
